import React from "react"
import { useTranslation } from "react-i18next"

import { AddIcon, Modal } from "components/Elements"
import { AddBikeForm } from "../AddBikeForm"

import "./AddButton.scoped.scss"
import {
	bikeRegisteredEnum,
	BikeRegistrationActionType,
	useBikeRegistrationProcess,
} from "features/dashboard/hooks"

type AddBikeButtonProps = {
	className?: string
	refreshBikes: () => Promise<void>
	forceTriggering?: boolean
	focusRedirectionRef?: React.MutableRefObject<HTMLElement>
}

export const AddBikeButton: React.FC<AddBikeButtonProps> = ({
	className,
	refreshBikes,
	forceTriggering = false,
	focusRedirectionRef,
}) => {
	const { bikeRegistrationDispatch, bikeRegistrationState } = useBikeRegistrationProcess()
	const [isModalOpen, setModalIsOpen] = React.useState<boolean>(forceTriggering)
	const { t } = useTranslation()

	React.useEffect(() => {
		// if forceTriggering changed, we need to update the modal state
		// Lorsqu'un utilisateur se connecte et qu'il n'a pas de vélo, on veut afficher la modale d'ajout. On fait donc un "forceTriggering"
		// On passe par un useEffect car l'appel au getBikes est asynchrone. Il faut attendre que l'appel soit terminé pour savvoir si notre tableau de bikes est vide ou non
		if (isModalOpen !== forceTriggering) setModalIsOpen(forceTriggering)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [forceTriggering])

	const isRegistrationInitialState = bikeRegistrationState.step === bikeRegisteredEnum.initialStep

	const resetBikeRegistration = () => {
		if (!isRegistrationInitialState) {
			bikeRegistrationDispatch({
				type: BikeRegistrationActionType.INITIAL_STEP,
			})
		}
	}

	const onModalClose = () => {
		resetBikeRegistration()
		// Design requirement: modal closing should not restore focus on triggering button (browser behavior).
		// Instead the focus is restored on any other given ref
		if (focusRedirectionRef) focusRedirectionRef.current.focus()
	}

	return (
		<>
			<button
				type="button"
				onClick={() => setModalIsOpen((prevIsModalOpen) => !prevIsModalOpen)}
				className={`c-add-btn ${className}`}
			>
				<AddIcon size="80" color="var(--secondaryColorLighten)" className="m-3" />
				{t("add_bike", { ns: "bike" })}
			</button>

			<Modal
				title={t("add_bike", { ns: "bike" })}
				isOpen={isModalOpen}
				setIsOpen={setModalIsOpen}
				className={"max-w-lg"}
				onClose={onModalClose}
			>
				<AddBikeForm setModalIsOpen={setModalIsOpen} refreshBikes={refreshBikes} />
			</Modal>
		</>
	)
}
